import letrusAPI from 'APIs/letrusAPI/axiosInstance';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import {setUserData} from 'utils/functions/getUserData/getUserData';
import {
  ResetPassword,
  UserData,
  UserProfile,
  UserProfileListResponse,
} from './types';

// GET /api/v1/me/ (fetchUserDataRequest, below) intentionally does NOT carry
// recent_transfer_notice — that field is only wired to a dedicated serializer
// on this endpoint, to avoid an extra query on every login/user listing (see
// UserDetailSerializer in the backend). Callers that need the class-transfer
// notice (BO-2277) must fetch it here instead, not from the cached session.
export function fetchUserProfileRequest() {
  return useSWR<UserProfileListResponse>(
    '/api/v1/user_profile/',
    (url) => letrusAPI(url).then((response) => response.data),
    {shouldRetryOnError: false, revalidateOnFocus: false, dedupingInterval: 0},
  );
}

export function fetchUserDataRequest() {
  return useSWRMutation<UserData, Error, string, null>(
    `/api/v1/me/`,
    async (url) => {
      try {
        const response = await letrusAPI(url, {method: 'get'});

        setUserData(response.data);

        return response.data;
      } catch (error: any) {
        throw new Error(error.message ?? '');
      }
    },
    {throwOnError: false},
  );
}

export function updateUserProfileRequest() {
  return useSWRMutation<null, Error, string, Partial<UserProfile>>(
    '/api/v1/user_profile/',
    async (url, {arg}) => {
      const {id, ...data} = arg;
      try {
        const response = await letrusAPI(`${url}${id}`, {
          method: 'patch',
          data,
        });

        return response.data;
      } catch (error: any) {
        throw new Error(error.message ?? '');
      }
    },
    {throwOnError: false},
  );
}

export function resetPasswordRequest() {
  return useSWRMutation<null, Error, string, ResetPassword>(
    `/api/v1/users`,
    async (url, {arg: {userId, ...data}}) => {
      return letrusAPI(`${url}/${userId}/reset_password/`, {
        method: 'post',
        data,
      })
        .then((response) => response.data)
        .catch((error) => {
          const errorPayload = error.response?.data;
          const apiError = errorPayload
            ? Object.entries(errorPayload)?.[0]
            : [];

          throw Error(apiError[0], {cause: apiError[1]});
        });
    },
    {throwOnError: false},
  );
}
